import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { Plus, Building2, X, Search, CheckCircle, AlertTriangle, AlertCircle, Activity } from 'lucide-react';
import { useToast } from '../context/ToastContext';

function HealthBadge({ score, label }) {
  const config = label === 'healthy'
    ? { icon: CheckCircle, color: 'text-accent-green', bg: 'bg-accent-green/15' }
    : label === 'at_risk'
      ? { icon: AlertTriangle, color: 'text-accent-yellow', bg: 'bg-accent-yellow/15' }
      : { icon: AlertCircle, color: 'text-accent-red', bg: 'bg-accent-red/15' };
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded ${config.bg} ${config.color}`} title={`Helsescore: ${score}/100`}>
      <Icon size={10} /> {score}
    </span>
  );
}

function CallRecencyDot({ lastCallAt }) {
  // Green: <7d, Yellow: 7-30d, Red: >30d or never
  let color = 'bg-accent-red';
  let label = 'Inaktiv';
  let labelClasses = 'bg-red-500/15 text-red-400 border-red-500/30';
  let title = 'Ingen samtaler ennå';
  if (lastCallAt) {
    const days = Math.floor((Date.now() - new Date(lastCallAt).getTime()) / 86400000);
    if (days < 7) {
      color = 'bg-accent-green';
      label = 'Aktiv';
      labelClasses = 'bg-green-500/15 text-green-400 border-green-500/30';
      title = `Sist samtale: ${days === 0 ? 'i dag' : days === 1 ? 'i går' : `${days} dager siden`}`;
    } else if (days <= 30) {
      color = 'bg-accent-yellow';
      label = 'Stille';
      labelClasses = 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30';
      title = `Sist samtale: ${days} dager siden`;
    } else {
      color = 'bg-accent-red';
      label = 'Inaktiv';
      labelClasses = 'bg-red-500/15 text-red-400 border-red-500/30';
      title = `Sist samtale: ${days} dager siden`;
    }
  }
  return (
    <span
      className="relative group inline-flex items-center gap-1.5"
      data-testid="call-recency-dot"
    >
      <span
        className={`inline-block w-2 h-2 rounded-full ${color} flex-shrink-0`}
        title={title}
        aria-label={title}
      />
      <span
        className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${labelClasses}`}
      >
        {label}
      </span>
      {/* Proper hover tooltip (visible on group-hover) so the date is readable on touch + desktop */}
      <span
        role="tooltip"
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-1 z-20 hidden group-hover:block whitespace-nowrap text-xs px-2 py-1 rounded bg-navy-900 text-slate-200 border border-navy-600 shadow-lg"
      >
        {title}
      </span>
    </span>
  );
}

function PaymentBadge({ status }) {
  if (!status || status === 'active') return null;
  if (status === 'past_due') {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 font-medium border border-red-500/30 ml-1">
        Forfallt
      </span>
    );
  }
  if (status === 'canceled') {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-500/20 text-slate-400 font-medium border border-slate-500/30 ml-1">
        Kansellert
      </span>
    );
  }
  return null;
}

const PAYMENT_FILTERS = [
  { label: 'Alle', value: 'all' },
  { label: 'Aktive', value: 'active' },
  { label: 'Forfallt', value: 'past_due' },
  { label: 'Kansellert', value: 'canceled' },
];

export default function AdminCompanies() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(() => searchParams.get('new') === '1');
  const [form, setForm] = useState({ name: '', agent_id: '', phone_number: '', language: 'no' });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState(() => searchParams.get('q') || '');
  const [selected, setSelected] = useState(new Set());
  const [paymentFilter, setPaymentFilter] = useState(() => searchParams.get('payment') || 'all');
  const [planFilter, setPlanFilter] = useState(() => searchParams.get('plan') || 'all');

  useEffect(() => {
    loadCompanies();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync search/filters to URL
  useEffect(() => {
    const params = {};
    if (search) params.q = search;
    if (paymentFilter && paymentFilter !== 'all') params.payment = paymentFilter;
    if (planFilter && planFilter !== 'all') params.plan = planFilter;
    setSearchParams(params, { replace: true });
  }, [search, paymentFilter, planFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCompanies = async () => {
    try {
      const { data } = await api.get('/api/admin/companies?limit=200');
      setCompanies(data.companies);
    } catch (err) {
      console.error(err);
      toast.error('Kunne ikke laste bedriftsliste');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    try {
      await api.post('/api/admin/companies', form);
      setShowModal(false);
      setForm({ name: '', agent_id: '', phone_number: '', language: 'no' });
      loadCompanies();
    } catch (err) {
      setError(err.response?.data?.detail || 'Noe gikk galt');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div data-testid="admin-companies-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white tracking-tight">Bedrifter</h1>
          <p className="text-slate-500 text-sm mt-1">Administrer alle klientbedrifter</p>
        </div>
        <button
          data-testid="create-company-button"
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-accent-blue hover:bg-accent-blue-hover text-white font-medium rounded-lg transition-colors text-sm"
        >
          <Plus size={16} /> Ny bedrift
        </button>
      </div>

      {/* Payment filter tabs */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {PAYMENT_FILTERS.map(f => (
          <button
            key={f.value}
            onClick={() => setPaymentFilter(f.value)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              paymentFilter === f.value
                ? 'bg-accent-blue text-white'
                : 'bg-navy-800 border border-navy-600 text-slate-400 hover:border-accent-blue hover:text-white'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Search + bulk actions */}
      <div className="mb-6 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            placeholder="Søk etter bedrift..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="admin-companies-search"
            className="w-full pl-10 pr-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue"
          />
        </div>
        <select
          value={planFilter}
          onChange={(e) => setPlanFilter(e.target.value)}
          data-testid="admin-plan-filter"
          className="px-3 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue appearance-none cursor-pointer"
        >
          <option value="all">Alle planer</option>
          <option value="Start">Start</option>
          <option value="Pro">Pro</option>
          <option value="Bedrift">Bedrift</option>
        </select>

        {selected.size > 0 && (
          <div className="flex items-center gap-2 bg-navy-800 border border-accent-blue/40 rounded-lg px-3 py-2" data-testid="bulk-actions-bar">
            <span className="text-sm text-white">{selected.size} valgt</span>
            <button
              onClick={async () => {
                if (!window.confirm(`Aktiver ${selected.size} bedrifter?`)) return;
                try {
                  await Promise.all(Array.from(selected).map(id => api.put(`/api/admin/companies/${id}`, { active: true })));
                  toast.success(`${selected.size} bedrifter aktivert`);
                  setSelected(new Set());
                  loadCompanies();
                } catch (err) {
                  toast.error('Kunne ikke aktivere alle bedrifter');
                }
              }}
              className="text-xs px-2.5 py-1.5 bg-accent-green/20 text-accent-green hover:bg-accent-green/30 rounded transition-colors"
              data-testid="bulk-activate"
            >
              Aktiver
            </button>
            <button
              onClick={async () => {
                if (!window.confirm(`Deaktiver ${selected.size} bedrifter?`)) return;
                try {
                  await Promise.all(Array.from(selected).map(id => api.put(`/api/admin/companies/${id}`, { active: false })));
                  toast.warning(`${selected.size} bedrifter deaktivert`);
                  setSelected(new Set());
                  loadCompanies();
                } catch (err) {
                  toast.error('Kunne ikke deaktivere alle bedrifter');
                }
              }}
              className="text-xs px-2.5 py-1.5 bg-accent-red/20 text-accent-red hover:bg-accent-red/30 rounded transition-colors"
              data-testid="bulk-deactivate"
            >
              Deaktiver
            </button>
            <button
              onClick={() => setSelected(new Set())}
              className="text-xs px-2.5 py-1.5 text-slate-400 hover:text-white transition-colors"
            >
              Tøm valg
            </button>
          </div>
        )}
      </div>

      {/* Companies grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          [...Array(6)].map((_, i) => <div key={i} className="h-36 bg-navy-800 rounded-lg animate-pulse" />)
        ) : companies
          .filter(c => !search || (c.name || '').toLowerCase().includes(search.toLowerCase()) || (c.phone_number || '').toLowerCase().includes(search.toLowerCase()))
          .filter(c => {
            if (paymentFilter === 'all') return true;
            if (paymentFilter === 'active') return !c.payment_status || c.payment_status === 'active';
            return c.payment_status === paymentFilter;
          })
          .filter(c => {
            if (planFilter === 'all') return true;
            const p = (c.plan || c.plan_name || '').toLowerCase();
            return p === planFilter.toLowerCase();
          })
          .map((c) => (
          <div
            key={c._id}
            data-testid={`company-card-${c._id}`}
            className={`relative bg-navy-800 border rounded-lg p-5 hover:border-accent-blue cursor-pointer transition-all group ${selected.has(c._id) ? 'border-accent-blue ring-1 ring-accent-blue/40' : 'border-navy-600'}`}
          >
            <input
              type="checkbox"
              checked={selected.has(c._id)}
              onChange={(e) => {
                e.stopPropagation();
                const next = new Set(selected);
                if (next.has(c._id)) next.delete(c._id); else next.add(c._id);
                setSelected(next);
              }}
              onClick={(e) => e.stopPropagation()}
              className="absolute top-3 left-3 w-3.5 h-3.5 rounded border-navy-600 bg-navy-900 text-accent-blue focus:ring-accent-blue cursor-pointer"
              data-testid={`company-checkbox-${c._id}`}
            />
            <div onClick={() => navigate(`/admin/companies/${c._id}`)}>
              <div className="flex items-center justify-between mb-3 ml-5">
                <div className="w-10 h-10 bg-accent-blue/15 rounded-lg flex items-center justify-center">
                  <Building2 size={18} className="text-accent-blue" />
                </div>
                <div className="flex items-center gap-1.5">
                  {c.health_score !== undefined && (
                    <HealthBadge score={c.health_score} label={c.health_label} />
                  )}
                  <span className={`text-xs px-2 py-1 rounded ${c.active ? 'bg-accent-green/15 text-accent-green' : 'bg-accent-red/15 text-accent-red'}`}>
                    {c.active ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </div>
              </div>
              <h3 className="font-heading text-base font-semibold text-white group-hover:text-accent-blue transition-colors ml-5 flex items-center gap-2">
                <CallRecencyDot lastCallAt={c.last_call_at} />
                {c.name}
              </h3>
              <p className="text-xs text-slate-500 mt-1 font-mono ml-5">{c.phone_number}</p>
              <div className="flex items-center gap-3 mt-2 ml-5">
                <div className="flex items-center gap-1 text-xs text-slate-500" title="Samtaler siste 7 dager">
                  <Activity size={11} /> {c.calls_this_week ?? 0}/uke
                </div>
                {c.last_login && (
                  <div className="text-xs text-slate-500" title={`Sist innlogget: ${new Date(c.last_login).toLocaleString('nb-NO')}`}>
                    • {(() => {
                      const d = Math.floor((Date.now() - new Date(c.last_login).getTime()) / 86400000);
                      return d === 0 ? 'i dag' : d === 1 ? '1 dag siden' : `${d} dager siden`;
                    })()}
                  </div>
                )}
              </div>
              <div className="mt-2 ml-5">
                <PaymentBadge status={c.payment_status} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4" data-testid="create-company-modal">
          <div className="bg-navy-800 border border-navy-600 rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-lg font-semibold text-white">Opprett ny bedrift</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-500 hover:text-white"><X size={18} /></button>
            </div>

            {error && <div className="bg-accent-red/10 border border-accent-red/20 text-accent-red px-3 py-2 rounded-lg mb-4 text-sm">{error}</div>}

            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Bedriftsnavn</label>
                <input
                  data-testid="company-name-input"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue"
                  placeholder="Bedrift AS"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Retell Agent ID</label>
                <input
                  data-testid="company-agent-id-input"
                  type="text"
                  value={form.agent_id}
                  onChange={(e) => setForm({ ...form, agent_id: e.target.value })}
                  className="w-full px-4 py-3 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue"
                  placeholder="oBeDLoLOeuAbiuaMFXRtDOLriTJ5tSxD"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Telefonnummer</label>
                <input
                  data-testid="company-phone-input"
                  type="text"
                  value={form.phone_number}
                  onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                  className="w-full px-4 py-3 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue"
                  placeholder="+4712345678"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Portalspråk</label>
                <select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}
                  className="w-full px-4 py-3 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue">
                  <option value="no">🇳🇴 Norsk</option>
                  <option value="en">🇺🇸 English</option>
                </select>
              </div>
              <button
                data-testid="submit-create-company"
                type="submit"
                disabled={creating}
                className="w-full py-3 bg-accent-blue hover:bg-accent-blue-hover text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                {creating ? 'Oppretter...' : 'Opprett bedrift'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
